import AppCard from '@nxsys/components/AppCard';
import AppCheckbox from '@nxsys/components/AppCheckbox';
// import AppCheckboxGroup from '@nxsys/components/AppCheckboxGroup';
import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppSelect from '@nxsys/components/AppSelect';
import AppSelectSync, { OptionType } from '@nxsys/components/AppSelectSync';
import {
  FORM_STATUTORY_TYPE,
  LEAVE_DURATION_OPTION,
  QUALIFYING_DAYS,
  STATUTORY_PAYMENT_TYPE,
} from '@nxsys/constants/statutory';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { getAWEStatutory } from '@nxsys/store/slices/statutory/action';
import {
  CreateStatutoryParams,
  GetAWEParams,
} from '@nxsys/store/slices/statutory/types';
import { Form } from 'antd';
import { FormProps } from 'antd/lib';
import { find, toNumber } from 'lodash';
import moment, { Moment } from 'moment';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

type FormStatutoryProps = {
  type: (typeof STATUTORY_PAYMENT_TYPE)[keyof typeof STATUTORY_PAYMENT_TYPE];
  formType: (typeof FORM_STATUTORY_TYPE)[keyof typeof FORM_STATUTORY_TYPE];
  onSubmit: (values: any) => void;
  formRef?: any;
};

type FormItemsType = {
  label: string;
  name: string;
  type: string;
  apiType?: string;
  selectSyncType?: string;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  options?: { label: string; value: string }[];
};

const FormStatutory: React.FC<FormStatutoryProps> = ({
  type,
  formType,
  onSubmit,
  formRef,
}: FormStatutoryProps) => {
  const { messages } = useTypedIntl();
  const [formInner] = Form.useForm();
  const form = formRef ?? formInner;
  const [qualifyingDays, setQualifyingDays] = useState<string[]>([]);
  const dispatch = useDispatch<AppDispatch>();
  const watchSubcontractor = Form.useWatch('subcontractor', form);
  const watchExpectedWeekOfChildbirth = Form.useWatch(
    'expectedWeekOfChildbirth',
    form,
  );
  const watchMatchNotifiedDate = Form.useWatch('matchNotifiedDate', form);
  const watchNotifiedLeaveDate = Form.useWatch('notifiedLeaveDate', form);
  const watchBabyBornDate = Form.useWatch('babyBornDate', form);
  const watchPregnancyRelatedIllnessStartedOn = Form.useWatch(
    'pregnancyRelatedIllnessStartedOn',
    form,
  );
  const isFirstMppCascade = useRef(true);

  const FORM_ITEMS: FormItemsType[] = [
    {
      label: messages['statutory.form.employeeSubcontractor'],
      name: 'subcontractor',
      type: 'selectSync',
      apiType: 'searchEmployeeSubcontractV2',
      selectSyncType: 'emp&sub',
      required: true,
      placeholder: messages['statutory.form.selectEmployee'],
      disabled: formType === FORM_STATUTORY_TYPE.UPDATE,
    },
    ...(type === STATUTORY_PAYMENT_TYPE.SSP
      ? [
          {
            label: messages['statutory.form.piwStartDate'],
            name: 'startDate',
            type: 'date',
            required: true,
          },
          {
            label: messages['statutory.form.piwEndDate'],
            name: 'endDate',
            type: 'date',
            required: true,
          },
        ]
      : []),
    ...(type === STATUTORY_PAYMENT_TYPE.SMP ||
    type === STATUTORY_PAYMENT_TYPE.SPP_BIRTH
      ? [
          {
            label: messages['statutory.form.expectedWeekOfChildbirth'],
            name: 'expectedWeekOfChildbirth',
            type: 'date',
            required: true,
            placeholder: messages['statutory.form.selectDate'],
          },
          ...(type === STATUTORY_PAYMENT_TYPE.SMP
            ? [
                {
                  label: messages['statutory.form.notifiedLeaveDate'],
                  name: 'notifiedLeaveDate',
                  type: 'date',
                  required: true,
                  placeholder: messages['statutory.form.selectDate'],
                },
                {
                  label: messages['statutory.form.mppStartDate'],
                  name: 'mppStartDate',
                  type: 'date',
                  required: true,
                  placeholder: messages['statutory.form.selectDate'],
                },
              ]
            : []),
          {
            label: messages['statutory.form.babyBornDate'],
            name: 'babyBornDate',
            type: 'date',
            placeholder: messages['statutory.form.selectDate'],
          },
          ...(type === STATUTORY_PAYMENT_TYPE.SPP_BIRTH
            ? [
                {
                  label: messages['statutory.form.notifiedLeaveStart'],
                  name: 'notifiedLeaveStart',
                  type: 'date',
                  required: true,
                  placeholder: messages['statutory.form.selectDate'],
                },
              ]
            : []),
          ...(type === STATUTORY_PAYMENT_TYPE.SMP
            ? [
                {
                  label:
                    messages['statutory.form.pregnancyRelatedIllnessStartedOn'],
                  name: 'pregnancyRelatedIllnessStartedOn',
                  type: 'date',
                  placeholder: messages['statutory.form.selectDate'],
                },
              ]
            : []),
        ]
      : []),
    ...(type === STATUTORY_PAYMENT_TYPE.SAP ||
    type === STATUTORY_PAYMENT_TYPE.SPP_ADOPTION
      ? [
          {
            label: messages['statutory.form.matchNotifiedDate'],
            name: 'matchNotifiedDate',
            type: 'date',
            required: true,
            placeholder: messages['statutory.form.selectDate'],
          },
          {
            label: messages['statutory.form.expectedPlacementDate'],
            name: 'expectedPlacementDate',
            type: 'date',
            required: false,
            placeholder: messages['statutory.form.selectDate'],
          },
          ...(type === STATUTORY_PAYMENT_TYPE.SAP
            ? [
                {
                  label: messages['statutory.form.startDateOfSAP'],
                  name: 'startDateOfSAP',
                  type: 'date',
                  required: true,
                  placeholder: messages['statutory.form.selectDate'],
                },
              ]
            : []),
          ...(type === STATUTORY_PAYMENT_TYPE.SPP_ADOPTION
            ? [
                {
                  label: messages['statutory.form.startDateOfSPP'],
                  name: 'startDateOfSPP',
                  type: 'date',
                  required: true,
                  placeholder: messages['statutory.form.selectDate'],
                },
              ]
            : []),
          {
            label: messages['statutory.form.actualPlacementDate'],
            name: 'actualPlacementDate',
            type: 'date',
            required: false,
            placeholder: messages['statutory.form.selectDate'],
          },
          ...(type === STATUTORY_PAYMENT_TYPE.SPP_ADOPTION
            ? [
                {
                  label: messages['statutory.form.howLongPaternityLeave'],
                  name: 'leaveDuration',
                  type: 'select',
                  options: LEAVE_DURATION_OPTION,
                  placeholder: messages['statutory.form.selectDuration'],
                  required: true,
                },
              ]
            : []),
          {
            label: messages['statutory.form.matchWeekDate'],
            name: 'matchWeekDate',
            type: 'date',
            required: false,
            disabled: true,
            placeholder: messages['statutory.form.autoPopulate'],
          },
        ]
      : []),
    ...(type === STATUTORY_PAYMENT_TYPE.SPP_BIRTH
      ? [
          {
            label: messages['statutory.form.howLongPaternityLeave'],
            name: 'leaveDuration',
            type: 'select',
            options: LEAVE_DURATION_OPTION,
            placeholder: messages['statutory.form.selectDuration'],
            required: true,
          },
        ]
      : []),
    ...(type !== STATUTORY_PAYMENT_TYPE.SSP
      ? [
          {
            label: messages['statutory.form.eightWeekAverageDate'],
            name: 'eightWeekAverageDate',
            type: 'text',
            disabled: true,
            placeholder: messages['statutory.form.autoPopulate'],
          },
          ...(type === STATUTORY_PAYMENT_TYPE.SMP ||
          type === STATUTORY_PAYMENT_TYPE.SPP_BIRTH
            ? [
                {
                  label: messages['statutory.form.qualifyingWeek'],
                  name: 'qualifyingWeek',
                  type: 'text',
                  disabled: true,
                  placeholder: messages['statutory.form.autoPopulate'],
                },
              ]
            : []),
          {
            label: messages['statutory.form.averagePay'],
            name: 'averagePay',
            type: 'text',
            disabled: true,
            placeholder: '0.00',
          },
          {
            label: messages['statutory.form.averagePayManual'],
            name: 'averagePayManual',
            type: 'number',
            placeholder: messages['statutory.form.enterAmount'],
          },
        ]
      : []),
  ];

  const onFinish: FormProps['onFinish'] = async (values) => {
    let params: CreateStatutoryParams =
      formType === FORM_STATUTORY_TYPE.CREATE
        ? {
            type,
            subcontractorId: values?.subcontractor?.value,
          }
        : ({} as any);
    if (type === STATUTORY_PAYMENT_TYPE.SSP) {
      params = {
        ...params,
        startDate: moment(values?.startDate)
          .tz('Europe/London')
          .format('DD/MM/YYYY'),
        endDate: moment(values?.endDate)
          .tz('Europe/London')
          .format('DD/MM/YYYY'),
        settingQualifyingDays: qualifyingDays,
      };
    }
    if (type === STATUTORY_PAYMENT_TYPE.SMP) {
      params = {
        ...params,
        notifiedLeaveStart: values?.notifiedLeaveDate,
        mppStartDate: values?.mppStartDate,
        illnessStartOn: values?.pregnancyRelatedIllnessStartedOn,
        expectedChildBirthWeek: values?.expectedWeekOfChildbirth,
        bornDate: values?.babyBornDate,
        averagePayManual: toNumber(values?.averagePayManual),
      };
    }
    if (type === STATUTORY_PAYMENT_TYPE.SAP) {
      params = {
        ...params,
        startDate: values?.startDateOfSAP,
        matchNotifiedDate: values?.matchNotifiedDate,
        expectedPlacementDate: values?.expectedPlacementDate,
        actualPlacementDate: values?.actualPlacementDate,
        averagePayManual: toNumber(values?.averagePayManual),
      };
    }
    if (type === STATUTORY_PAYMENT_TYPE.SPP_ADOPTION) {
      params = {
        ...params,
        startDate: values?.startDateOfSPP,
        matchNotifiedDate: values?.matchNotifiedDate,
        expectedPlacementDate: values?.expectedPlacementDate,
        actualPlacementDate: values?.actualPlacementDate,
        leaveDuration: values?.leaveDuration?.value ?? values?.leaveDuration,
        averagePayManual: toNumber(values?.averagePayManual),
      };
    }
    if (type === STATUTORY_PAYMENT_TYPE.SPP_BIRTH) {
      params = {
        ...params,
        notifiedLeaveStart: values?.notifiedLeaveStart,
        leaveDuration: values?.leaveDuration?.value ?? values?.leaveDuration,
        expectedChildBirthWeek: values?.expectedWeekOfChildbirth,
        bornDate: values?.babyBornDate,
        averagePayManual: toNumber(values?.averagePayManual),
      };
    }
    onSubmit(params);
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  const handleGetAWE = async (params: GetAWEParams) => {
    const res = await dispatch(getAWEStatutory(params)).unwrap();
    const { awe, fromDate, toDate, qualifyingWeek, matchWeekDate } = res as any;
    if (
      type === STATUTORY_PAYMENT_TYPE.SMP ||
      type === STATUTORY_PAYMENT_TYPE.SPP_BIRTH
    ) {
      form.setFieldsValue({
        averagePay: awe,
        eightWeekAverageDate: `${moment(fromDate)
          .tz('Europe/London')
          .format('DD/MM/YYYY')} - ${moment(toDate)
          .tz('Europe/London')
          .format('DD/MM/YYYY')}`,
        qualifyingWeek: `${moment(qualifyingWeek?.fromDate)
          .tz('Europe/London')
          .format('DD/MM/YYYY')} - ${moment(qualifyingWeek?.toDate).format(
          'DD/MM/YYYY',
        )}`,
      });
    }
    if (
      type === STATUTORY_PAYMENT_TYPE.SAP ||
      type === STATUTORY_PAYMENT_TYPE.SPP_ADOPTION
    ) {
      form.setFieldsValue({
        averagePay: awe,
        matchWeekDate: matchWeekDate,
        eightWeekAverageDate: `${moment(fromDate)
          .tz('Europe/London')
          .format('DD/MM/YYYY')} - ${moment(toDate)
          .tz('Europe/London')
          .format('DD/MM/YYYY')}`,
        qualifyingWeek: `${moment(qualifyingWeek?.fromDate)
          .tz('Europe/London')
          .format('DD/MM/YYYY')} - ${moment(qualifyingWeek?.toDate).format(
          'DD/MM/YYYY',
        )}`,
      });
    }
  };

  useEffect(() => {
    const values = form.getFieldsValue(true);
    let params: GetAWEParams = {} as any;
    if (
      (type === STATUTORY_PAYMENT_TYPE.SMP ||
        type === STATUTORY_PAYMENT_TYPE.SPP_BIRTH) &&
      values?.subcontractor &&
      values?.expectedWeekOfChildbirth
    ) {
      params = {
        type,
        subcontractorId: values?.subcontractor?.value,
        date: values?.expectedWeekOfChildbirth,
      };
    }
    if (
      (type === STATUTORY_PAYMENT_TYPE.SAP ||
        type === STATUTORY_PAYMENT_TYPE.SPP_ADOPTION) &&
      values?.subcontractor &&
      values?.matchNotifiedDate
    ) {
      params = {
        type,
        subcontractorId: values?.subcontractor?.value,
        matchNotifiedDate: values?.matchNotifiedDate,
      };
    }
    if (Object.keys(params).length > 0) handleGetAWE(params);
  }, [
    watchSubcontractor,
    watchExpectedWeekOfChildbirth,
    watchMatchNotifiedDate,
  ]);

  useEffect(() => {
    if (type !== STATUTORY_PAYMENT_TYPE.SMP) return;
    if (isFirstMppCascade.current) {
      isFirstMppCascade.current = false;
      return;
    }
    let next: string | null = null;
    if (watchBabyBornDate) {
      next = moment(watchBabyBornDate).add(1, 'day').toISOString();
    } else if (watchPregnancyRelatedIllnessStartedOn) {
      next = moment(watchPregnancyRelatedIllnessStartedOn).toISOString();
    } else if (watchNotifiedLeaveDate) {
      next = moment(watchNotifiedLeaveDate).toISOString();
    }
    if (next) {
      form.setFieldsValue({ mppStartDate: next });
    }
  }, [
    type,
    watchBabyBornDate,
    watchPregnancyRelatedIllnessStartedOn,
    watchNotifiedLeaveDate,
  ]);

  const { statutoryDetail } = useSelector(
    (state: AppState) => state.statutoryStore,
  );
  useEffect(() => {
    if (formType === FORM_STATUTORY_TYPE.UPDATE) {
      setQualifyingDays(statutoryDetail?.settingQualifyingDays ?? []);
    }
  }, [formType]);
  return (
    <AppFormContainer
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      initialValues={{
        subcontractor:
          formType === FORM_STATUTORY_TYPE.CREATE
            ? null
            : {
                label: statutoryDetail?.subcontractor?.fullName,
                value: statutoryDetail?.subcontractor?.id,
              },
        startDate:
          formType === FORM_STATUTORY_TYPE.CREATE
            ? null
            : moment(statutoryDetail?.startDate, 'YYYY-MM-DD'),
        endDate:
          formType === FORM_STATUTORY_TYPE.CREATE
            ? null
            : moment(statutoryDetail?.endDate, 'YYYY-MM-DD'),
        qualifyingDays:
          formType === FORM_STATUTORY_TYPE.CREATE
            ? []
            : statutoryDetail?.settingQualifyingDays,
        expectedWeekOfChildbirth:
          formType === FORM_STATUTORY_TYPE.CREATE
            ? null
            : statutoryDetail?.inputData?.expectedChildBirthWeek,
        notifiedLeaveDate:
          formType === FORM_STATUTORY_TYPE.CREATE
            ? null
            : statutoryDetail?.inputData?.notifiedLeaveStart,
        mppStartDate:
          formType === FORM_STATUTORY_TYPE.CREATE
            ? null
            : statutoryDetail?.inputData?.mppStartDate ||
              statutoryDetail?.inputData?.notifiedLeaveStart,
        babyBornDate:
          formType === FORM_STATUTORY_TYPE.CREATE
            ? null
            : statutoryDetail?.inputData?.bornDate,
        pregnancyRelatedIllnessStartedOn:
          formType === FORM_STATUTORY_TYPE.CREATE
            ? null
            : statutoryDetail?.inputData?.illnessStartOn,
        matchNotifiedDate:
          formType === FORM_STATUTORY_TYPE.CREATE
            ? null
            : statutoryDetail?.inputData?.matchNotifiedDate,
        expectedPlacementDate:
          formType === FORM_STATUTORY_TYPE.CREATE
            ? null
            : statutoryDetail?.inputData?.expectedPlacementDate,
        startDateOfSAP:
          formType === FORM_STATUTORY_TYPE.CREATE
            ? null
            : statutoryDetail?.inputData?.startDate,
        startDateOfSPP:
          formType === FORM_STATUTORY_TYPE.CREATE
            ? null
            : statutoryDetail?.inputData?.startDate,
        notifiedLeaveStart:
          formType === FORM_STATUTORY_TYPE.CREATE
            ? null
            : statutoryDetail?.inputData?.notifiedLeaveStart,
        actualPlacementDate:
          formType === FORM_STATUTORY_TYPE.CREATE
            ? null
            : statutoryDetail?.inputData?.actualPlacementDate,
        matchWeekDate:
          formType === FORM_STATUTORY_TYPE.CREATE
            ? null
            : statutoryDetail?.inputData?.matchWeekDate,
        leaveDuration: find(
          LEAVE_DURATION_OPTION,
          (item) => item.value === statutoryDetail?.inputData?.leaveDuration,
        ),
        averagePayManual:
          formType === FORM_STATUTORY_TYPE.CREATE
            ? null
            : statutoryDetail?.inputData?.averagePayManual,
      }}
    >
      {FORM_ITEMS.map((formItem) => (
        <Form.Item
          key={formItem.name}
          label={formItem.label}
          name={formItem.name}
          rules={[
            {
              required: formItem.required,
              message: messages['statutory.form.pleaseInput'],
            },
          ]}
        >
          {(formItem.type === 'text' || formItem.type === 'number') && (
            <AppInput
              name={formItem.name}
              type={formItem.type}
              placeholder={formItem.placeholder}
              onChange={(e) => {
                form.setFieldsValue({
                  [formItem.name]: e.target.value,
                });
              }}
              disabled={formItem?.disabled}
            />
          )}
          {formItem.type === 'select' && (
            <AppSelect
              placeholder={formItem.placeholder}
              options={formItem.options ?? []}
              onChange={(value) => {
                form.setFieldsValue({
                  [formItem.name]: value,
                });
              }}
              disabled={formItem?.disabled}
            />
          )}
          {formItem.type === 'selectSync' && (
            <AppSelectSync
              type={formItem.selectSyncType ?? ''}
              apiType={formItem.apiType as any}
              placeholder={formItem.placeholder}
              onChangeValue={(value: OptionType | OptionType[] | null) => {
                form.setFieldsValue({
                  [formItem.name]: value,
                });
              }}
              disabled={formItem?.disabled}
            />
          )}
          {formItem.type === 'date' && (
            <AppDatePicker
              placeholder={formItem.placeholder}
              onChange={(date: Moment) => {
                form.setFieldsValue({
                  [formItem.name]: date.toISOString(),
                });
              }}
              format="DD/MM/YYYY"
              disabled={formItem?.disabled}
            />
          )}
        </Form.Item>
      ))}
      {type === STATUTORY_PAYMENT_TYPE.SSP && (
        <Form.Item
          label={messages['statutory.form.selectQualifyingDays']}
          name="qualifyingDays"
          rules={[
            {
              required: true,
              message: messages['statutory.form.pleaseInput'],
            },
          ]}
        >
          <AppCard className="p-4">
            <div className="flex gap-x-3">
              <AppCheckbox
                checked={QUALIFYING_DAYS.every((day) =>
                  qualifyingDays?.includes(day),
                )}
                onChange={(e) => {
                  const newQualifyingDays = e.target.checked
                    ? QUALIFYING_DAYS
                    : [];
                  setQualifyingDays(newQualifyingDays);
                  form.setFieldsValue({ qualifyingDays: newQualifyingDays });
                }}
              />
              <div>{messages['statutory.form.selectAll']}</div>
            </div>
            <div className="p-4 flex flex-col gap-y-3">
              {/* <AppCheckboxGroup
                  options={QUALIFYING_DAYS.map(day => ({ label: day.charAt(0).toUpperCase() + day.slice(1).toLowerCase(), value: day }))}
                  value={currentQualifyingDays}
                  onChange={(value) => {
                    form.setFieldsValue({ qualifyingDays: value });
                  }}
                /> */}
              {QUALIFYING_DAYS.map((day) => (
                <div className="flex gap-x-3">
                  <AppCheckbox
                    checked={qualifyingDays?.includes(day)}
                    onChange={(e) => {
                      let newQualifyingDays = [];
                      if (e.target.checked) {
                        newQualifyingDays = [...qualifyingDays, day];
                      } else {
                        newQualifyingDays = qualifyingDays.filter(
                          (_day: string) => _day !== day,
                        );
                      }
                      setQualifyingDays(newQualifyingDays);
                      form.setFieldsValue({
                        qualifyingDays: newQualifyingDays,
                      });
                    }}
                  />
                  <div>
                    {day.charAt(0).toUpperCase() + day.slice(1).toLowerCase()}
                  </div>
                </div>
              ))}
            </div>
          </AppCard>
        </Form.Item>
      )}
    </AppFormContainer>
  );
};

export default FormStatutory;
